/**
 * useBookExpertConsultation Hook
 *
 * FERP Sprint 5 - Professional Features
 *
 * React hook for booking expert consultations via GraphQL mutation.
 *
 * Architecture Compliance:
 * - ✅ Repository pattern (wraps GraphQL mutation)
 * - ✅ Domain-agnostic (all data from backend)
 * - ✅ Life-critical safety (error handling)
 * - ✅ Multi-tenant security (tenant from context)
 * - ✅ Backend ID generation (consultation ID from backend)
 */

import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCurrentTenant } from './useCurrentTenant';
import { useAuth } from './useAuth';

/**
 * GraphQL mutation for booking expert consultation
 */
const BOOK_EXPERT_CONSULTATION_MUTATION = gql`
  mutation BookExpertConsultation($input: BookExpertConsultationInput!) {
    bookExpertConsultation(input: $input) {
      id
      tenant
      expertId
      userId
      scheduledAt
      duration
      status
      sessionType
      notes
      createdAt
    }
  }
`;

/**
 * Booking input interface
 */
export interface BookExpertConsultationInput {
  /**
   * Expert ID to book
   */
  expertId: string;

  /**
   * Scheduled date/time (ISO 8601)
   */
  scheduledAt: string;

  /**
   * Duration in minutes
   */
  duration: number;

  /**
   * Session type
   */
  sessionType: 'video' | 'audio' | 'phone';

  /**
   * Optional notes for consultation
   */
  notes?: string;
}

/**
 * Consultation result interface
 */
export interface ExpertConsultation {
  id: string;
  tenant: string;
  expertId: string;
  userId: string;
  scheduledAt: string;
  duration: number;
  status: 'scheduled' | 'completed' | 'cancelled';
  sessionType: 'video' | 'audio' | 'phone';
  notes?: string;
  createdAt: string;
}

/**
 * Hook result interface
 */
export interface UseBookExpertConsultationResult {
  /**
   * Book consultation function
   */
  bookConsultation: (input: BookExpertConsultationInput) => Promise<ExpertConsultation>;

  /**
   * Loading state
   */
  loading: boolean;

  /**
   * Error state
   */
  error: Error | undefined;
}

/**
 * useBookExpertConsultation Hook
 *
 * Provides mutation function to book expert consultations.
 * Automatically includes tenant and user context.
 *
 * @returns Booking mutation function and state
 *
 * @example
 * ```tsx
 * function BookingForm({ expert }) {
 *   const { bookConsultation, loading, error } = useBookExpertConsultation();
 *
 *   const handleBook = async () => {
 *     try {
 *       const consultation = await bookConsultation({
 *         expertId: expert.id,
 *         scheduledAt: selectedSlot.toISOString(),
 *         duration: 60,
 *         sessionType: 'video',
 *         notes: 'Tax consultation for small business'
 *       });
 *       console.log('Booked:', consultation.id);
 *     } catch (err) {
 *       console.error('Booking failed:', err);
 *     }
 *   };
 *
 *   return (
 *     <Button onClick={handleBook} loading={loading}>
 *       Book Consultation
 *     </Button>
 *   );
 * }
 * ```
 */
export function useBookExpertConsultation(): UseBookExpertConsultationResult {
  // ✅ FERP-Compliant: Get tenant from context (not hardcoded)
  const { tenantId } = useCurrentTenant();

  // ✅ FERP-Compliant: Get user from auth context (not hardcoded)
  const { currentUser } = useAuth();

  // GraphQL mutation
  const [mutate, { loading, error }] = useMutation<Record<string, any>>(
    BOOK_EXPERT_CONSULTATION_MUTATION
  );

  /**
   * Book consultation with automatic tenant/user context
   */
  const bookConsultation = async (
    input: BookExpertConsultationInput
  ): Promise<ExpertConsultation> => {
    if (!tenantId) {
      throw new Error('Tenant context required for booking');
    }

    if (!currentUser?.id) {
      throw new Error('User authentication required for booking');
    }

    // Execute mutation with tenant and user context
    const result = await mutate({
      variables: {
        input: {
          tenant: tenantId,
          expertId: input.expertId,
          userId: currentUser.id,
          scheduledAt: input.scheduledAt,
          duration: input.duration,
          sessionType: input.sessionType,
          notes: input.notes || '',
          createdBy: currentUser.id, // Audit trail
        },
      },
    });

    if (!result.data?.bookExpertConsultation) {
      throw new Error('Booking failed - no data returned');
    }

    return result.data?.bookExpertConsultation ?? null;
  };

  return {
    bookConsultation,
    loading,
    error,
  };
}
